
import { mapGetters, mapState } from "vuex"
import ImageCarousel from "@/components/business/ImageCarousel"
import BusinessHeader from "@/components/business/BusinessHeader"
import BusinessInfo from "@/components/business/BusinessInfo"
import breadcrumb from "@/mixins/breadcrumb"
import BusinessViewersSnackbar from "@/components/business/BusinessViewersSnackbar"
import JobList from "@/components/job/JobList"
import Button from "@/components/base/Button"
import traineeService from "@/services/trainee.service"
import chatSteps from "@/components/chat/chatSteps"
import FloatingChatButton from "@/components/chat/FloatingChatButton"
import { routeToBusiness } from "@/helpers/routing"
export default {
    components: {
        FloatingChatButton,
        ChatContainer: () => import("@/components/chat/ChatContainer"),
        ChatFormSuccess: () => import("@/components/chat/ChatFormSuccess"),
        Modal: () => import("@/components/base/modal/Modal"),
        ChatForm: () => import("@/components/application/interest/ChatForm"),
        BusinessViewersSnackbar,
        BusinessInfo,
        BusinessHeader,
        ImageCarousel,
        JobList,
        Button,
        BusinessSiteLocation: () =>
            import("@/components/business/BusinessSiteLocation"),
        PostList: () => import("@/components/post/PostList"),
    },
    mixins: [breadcrumb],
    fetch() {
        this.showChatComponent = this.$auth.loggedIn
        if ("nachricht_senden" in this.$route.query && this.$can.chat()) {
            this.sendMessageOnCreated = true
            this.showChatComponent = false
            this.isSending = true
            this.selectConversation()
            this.showChatModal = true
        }
    },
    async asyncData({ store, route, error }) {
        const businessSitePromise = store.dispatch(
            "business/setCurrentDisplayedProfileBySite",
            route.params.siteslug
        )
        const jobsPromise = store.dispatch(
            "business/loadJobsBySite",
            route.params.siteslug
        )
        const postPromise = store.dispatch(
            "post/getPostsByBusiness",
            route.params.slug
        )
        /** Await in parallel - allSettled Polyfill */
        const result = await Promise.all(
            [businessSitePromise, jobsPromise, postPromise].map((p) =>
                p
                    .then((value) => ({
                        status: "fulfilled",
                        value,
                    }))
                    .catch((reason) => ({
                        status: "rejected",
                        reason,
                    }))
            )
        )
        result.forEach((a) => {
            if (a.status === "rejected") {
                error({
                    statusCode: 404,
                    message: "Der Betrieb wurde nicht gefunden.",
                })
            }
        })
    },
    layout(context) {
        return context.$auth.user && context.$auth.user.business
            ? "modern"
            : "detailpage"
    },
    meta: {
        toolbar: {
            persistent: true,
            keepSearch: true,
            hideBottomNav: true,
            detailType: "business",
        },
        sidebar: {
            dense: true,
        },
    },
    data: () => ({
        chatSteps: null,
        sendMessageOnCreated: false,
        isFloatingChatVisible: true,
        showChatComponent: false,
        showChatModal: false,
        isSending: false,
        showSuccessModal: false,
        loadingLikeModal: true,
        showLikeModal: false,
        likeOnOpen: false,
        firstname: "",
        lastname: "",
    }),
    computed: {
        ...mapState({
            business: (state) => state.business.displayedProfile.baseInfo,
            apprenticeships: (state) =>
                state.business.displayedProfile.apprenticeships,
            traineeships: (state) =>
                state.business.displayedProfile.traineeships,
        }),
        ...mapGetters(["webpSupported"]),
        ...mapState("post", ["posts"]),
        socialRedirectUrl() {
            let route = { ...this.$route.fullPath }
            route.query = { ...route.query, ...{ nachricht_senden: true } }
            return this.$router.resolve(route).href
        },
        logo() {
            return this.$getImage({
                image: this.business.logo,
                preferedSize: "3",
            })
        },
        showBusinessMap() {
            return this.business.primary_address?.street?.length > 0
        },
        showFloatingChat() {
            return (
                !this.$auth.loggedIn ||
                (!this.$auth.user.business && !this.$auth.user.school)
            )
        },
    },
    mounted() {
        this.chatSteps = chatSteps

        if ("chat" in this.$route.query && this.$can.chat()) {
            this.openChat()
        }
    },
    methods: {
        async openChat() {
            this.isInRequest = false
            if (this.showChatModal) return
            await this.selectConversation()
            this.showChatModal = true
        },
        onChatAuth(formData) {
            this.firstname = formData.firstname.data
            this.lastname = formData.lastname.data
        },
        onChatFormInit(formData) {
            if (this.sendMessageOnCreated && this.$can.chat()) {
                this.selectConversation()
                this.chatSendRequest(formData)
                this.$router.replace(this.$route.path) // Remove query
            }
        },
        selectConversation() {
            return this.$store.dispatch("chat/selectConversation", {
                business: this.business,
            })
        },
        async chatSendRequest(formData) {
            if (!this.$auth.loggedIn || !this.$can.chat() || !formData) {
                this.handleSendRequestError(
                    {},
                    "Da ist etwas schief gelaufen. Bitte melde dich nochmal an."
                )
                return
            }
            try {
                this.isSending = true
                // send text message to conversation
                await this.$store.dispatch("chat/sendMessage", {
                    text: formData["messageText"].data ?? undefined,
                })

                // update trainee firstname/lastname
                const traineeId = this.$auth.user.trainee.id
                await traineeService
                    .update(traineeId, {
                        firstname: formData["firstname"].data ?? undefined,
                        lastname: formData["lastname"].data ?? undefined,
                    })
                    .catch((err) => console.warn(err))
                await this.$auth.fetchUser()
                this.$refs.chatForm?.remove()
                this.showChatModal = false
                this.showChatComponent = true
                this.showSuccessModal = true
                await this.$store.dispatch("chat/loadConversations")
            } catch (err) {
                console.error(err)
                this.handleSendRequestError(err)
            }
            this.isSending = false
        },
        onChatIntersect(e) {
            this.isFloatingChatVisible = !e[0].isIntersecting
        },
    },
    head() {
        return {
            title:
                this.business.name +
                ": Ausbildungsplätze in " +
                this.business.primary_address.city,
            link: [
                {
                    hid: "rel:canonical",
                    rel: "canonical",
                    href: routeToBusiness(this.business),
                },
            ],
        }
    },
}
